import {
  ReactNode,
  createContext,
  useContext,
  useEffect,
  useState,
  useRef,
  useCallback,
} from "react";
import { UserCompanyInfo } from "~/pages/login/types/login";
import { TOKEN } from "~/constants/storage";
import { removeCookieToken, setCookieToken } from "~/storages/cookie";
import { getToken } from "~/storages/token";
import { PAGE_LOGIN } from "~/constants/page";
import { parseJwt } from "~/helpers/jwt";
import { ParsedToken } from "~/types/authentication";
import { useRenewToken } from "~/hooks/useRenewToken";

interface AuthenticationContextProps {
  token: string | undefined;
  isAuthLoading: boolean;
  setIsAuthLoading: (isAuthLoading: boolean) => void;
  handleSetToken: (token: string) => void;
  handleRemoveToken: () => void;
  userCompanyInfos: UserCompanyInfo[] | undefined;
  setUserCompanyInfos: React.Dispatch<
    React.SetStateAction<UserCompanyInfo[] | undefined>
  >;
  username: string | undefined;
  setUsername: React.Dispatch<React.SetStateAction<string | undefined>>;
}

interface AuthenticationProviderProps {
  children: ReactNode;
}

const AuthenticationContext = createContext<AuthenticationContextProps>({
  token: undefined,
  isAuthLoading: false,
  setIsAuthLoading: () => {},
  handleSetToken: () => {},
  handleRemoveToken: () => {},
  userCompanyInfos: undefined,
  setUserCompanyInfos: () => {},
  username: undefined,
  setUsername: () => {},
});

const CHECK_TOKEN_RENEW_MS = 10000; // 10 seconds

const REFRESH_TOKEN_TIME = 3600; // 1 hour

export default function AuthenticationProvider({
  children,
}: AuthenticationProviderProps) {
  const [token, setToken] = useState<string | undefined>(getToken());

  const tokenRef = useRef<string | undefined>(getToken());

  const [userCompanyInfos, setUserCompanyInfos] = useState<UserCompanyInfo[]>();
  const [isAuthLoading, setIsAuthLoading] = useState(true);
  const [username, setUsername] = useState<string | undefined>(undefined);

  const renewTokenIntervalRef = useRef<NodeJS.Timeout | null>(null);

  const renewToken = useRenewToken();

  const handleSetToken = useCallback((newToken: string) => {
    if (newToken) {
      tokenRef.current = newToken;
      sessionStorage.setItem(TOKEN, newToken);
      setCookieToken(newToken);
      setToken(newToken);
      setIsAuthLoading(false);
    }
  }, []);

  const handleRemoveToken = useCallback(() => {
    sessionStorage.removeItem(TOKEN);
    removeCookieToken();
    setToken(undefined);
    window.location.href = PAGE_LOGIN;
  }, []);

  useEffect(() => {
    if (renewTokenIntervalRef.current)
      clearInterval(renewTokenIntervalRef.current);
    renewTokenIntervalRef.current = setInterval(async () => {
      if (tokenRef.current) {
        const parsedToken: ParsedToken = parseJwt(tokenRef.current);
        const currentTimeInSeconds = Math.floor(Date.now() / 1000);
        const tokenStartTimeInSeconds = parsedToken.nbf || parsedToken.iat;

        if (
          tokenStartTimeInSeconds + REFRESH_TOKEN_TIME <
          currentTimeInSeconds
        ) {
          try {
            const renewTokenResult = await renewToken();
            if (renewTokenResult === undefined) {
              handleRemoveToken();
            } else {
              handleSetToken(renewTokenResult);
            }
          } catch (error) {
            handleRemoveToken();
          }
        } else if (tokenRef.current == undefined || tokenRef.current === "") {
          handleRemoveToken();
        }
      }
    }, CHECK_TOKEN_RENEW_MS);
  }, [handleRemoveToken, handleSetToken, renewToken]);

  useEffect(() => {
    return () => {
      if (renewTokenIntervalRef.current)
        clearInterval(renewTokenIntervalRef.current);
    };
  }, []);

  return (
    <AuthenticationContext.Provider
      value={{
        token,
        isAuthLoading,
        setIsAuthLoading,
        handleSetToken,
        handleRemoveToken,
        userCompanyInfos,
        setUserCompanyInfos,
        username,
        setUsername,
      }}
    >
      {children}
    </AuthenticationContext.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export const useAuthenticationContext = () => useContext(AuthenticationContext); 